import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import useBillingDetailsValidation from 'pages/Settings/Payment/hooks/useBillingValidation';

import { COMPANY, OTO_LINK, WAREHOUSES } from 'services/consts';

import { setUserDetails, setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES } from 'utils/constants';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';

import { getDeliveryCompanies, getOTOLinkSettings } from '../OTOLinkSettings/OtoLinkSettingsSlice';
import PaymentMethodTab from '../OTOLinkSettings/PaymentMethodTab';
import ShippingMethodTab from '../OTOLinkSettings/ShippingMethodTab';
import * as S from './OTOLinkOnboarding.styled';
import AccountType from './Tabs/AccountType';
import BankDetails from './Tabs/BankDetails';
import VerificationAndAddress from './Tabs/VerificationAndAddress';

const OTOLinkOnboarding = NiceModal.create(() => {
	const modal = useModal();
	const isAuthorized = useAccessControl();
	const dispatch = useDispatch();
	const { showError, showSuccess } = useAlert();
	const { billingDefaults } = useBillingDetailsValidation();
	const axios = useAxiosPrivate();
	const { t } = useTranslation();
	const user = useSelector((state) => state.user);
	const { settings: otoLinkSettings, deliveryCompanies } = useSelector((state) => state.otoLinkSettings);

	const [tab, setTab] = useState(0);

	const verificationAndAddressSchema = useMemo(
		() =>
			yup.object().shape({
				isMobileVerified: yup.bool().oneOf([true], 'mobileVerificationRequired'),
				phone: yup
					.string()
					.nullable()
					.required()
					.when('isMobileVerified', (isMobileVerified, schema) =>
						isMobileVerified ? schema : schema.phoneNumber()
					)
					.phoneNumber(),
				address: yup.object().shape({
					text: yup.string().nullable().required(),
					countryName: yup.string().isCountryAllowed().nullable().required(),
					stateName: yup.string().nullable(),
					cityName: yup.string().nullable().required(),
					districtName: yup.string().nullable(),
					zipCode: yup.string().nullable(),
					addressInputType: yup.string().nullable()
				})
			}),
		[]
	);

	const accountTypeSchema = useMemo(
		() =>
			yup.object().shape({
				invoiceType: yup
					.string()
					.nullable()
					.oneOf([ACCOUNT_TYPES.PERSONAL, ACCOUNT_TYPES.COMPANY, ACCOUNT_TYPES.FREELANCER])
					.required(),
				notVATRegistered: yup.boolean(),
				CRNumber: yup
					.string()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) => {
						if (
							invoiceType === ACCOUNT_TYPES.COMPANY &&
							isAuthorized('/company-settings/CommonInvoiceType')
						) {
							return schema.checkCRNumber().required();
						}
						return schema;
					}),
				freelanceDocument: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) =>
						invoiceType === ACCOUNT_TYPES.FREELANCER ? schema.required() : schema
					),
				civilID: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) => {
						if (
							invoiceType === ACCOUNT_TYPES.FREELANCER &&
							isAuthorized('/company-settings/CommonInvoiceType')
						)
							return schema.required();
						if (invoiceType === ACCOUNT_TYPES.COMPANY && isAuthorized('/company-settings/KSAInvoiceType'))
							return schema.required();
						return schema;
					}),
				CRDocumentURL: yup
					.mixed()
					.nullable()
					.when('invoiceType', ([invoiceType], schema) =>
						invoiceType === ACCOUNT_TYPES.COMPANY ? schema.required() : schema
					),
				billingInfo: yup
					.object()
					.shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().nullable().required(),
						stateName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						cityName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						districtName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						buildingName: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) =>
									isAuthorized('/company-settings/KSAInvoiceType')
										? schema
												.min(
													4,
													t('validation.numberFieldLength', {
														length: 4,
														fieldName: t('buildingNo')
													})
												)
												.max(
													4,
													t('validation.numberFieldLength', {
														length: 4,
														fieldName: t('buildingNo')
													})
												)
												.matches(/\d/, t('validation.numberFieldPositive'))
												.required()
										: schema.required()
							}),
						street: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) => schema.required()
							}),
						zipCode: yup
							.string()
							.nullable()
							.when([], {
								is: () => isAuthorized('/company-settings/TRInvoiceType'),
								then: (schema) => schema,
								otherwise: (schema) =>
									isAuthorized('/company-settings/KSAInvoiceType')
										? schema
												.min(
													5,
													t('validation.numberFieldLength', {
														length: 5,
														fieldName: t('zipCode')
													})
												)
												.max(
													5,
													t('validation.numberFieldLength', {
														length: 5,
														fieldName: t('zipCode')
													})
												)
												.matches(/\d/, t('validation.numberFieldPositive'))
												.requiredZipCode()
										: schema.requiredZipCode()
							})
					})
					.when('invoiceType', ([invoiceType], schema) => {
						// this form only requires billing info for the company
						if (invoiceType === ACCOUNT_TYPES.COMPANY) {
							return schema;
						}
						return yup.object();
					})
			}),
		[]
	);

	const bankDetailsSchema = useMemo(
		() =>
			yup.object().shape({
				BankName: user.generalSettings?.BankIban ? yup.string().nullable() : yup.string().nullable().required(),
				BankHolderName: user.generalSettings?.BankIban
					? yup.string().nullable()
					: yup.string().nullable().required(),
				BankIban: user.generalSettings?.BankIban
					? yup.string().nullable()
					: yup.string().nullable().required().iban()
			}),
		[]
	);

	const paymentMethodSchema = useMemo(
		() =>
			yup.object().shape({
				paymentMethod: yup.array().required().min(1)
			}),
		[]
	);

	const shippingCompanySchema = useMemo(
		() =>
			yup.object().shape({
				allowedDCIds: yup.array().required().min(1),
				pickupType: yup.array().required()
			}),
		[]
	);

	const updateAccountType = async (data) => {
		try {
			const payload = {
				VATNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.VATNumber : null,
				CRNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRNumber : null,
				fullName: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.fullName : null,
				idNumber: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.idNumber : null,
				taxOffice: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.taxOffice : null,
				notVATRegistered: data.notVATRegistered,
				companyName: data.companyName,
				CRDocumentURL:
					data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRDocumentURL?.path || data?.CRDocumentURL : null,
				freelanceDocument:
					data.invoiceType === ACCOUNT_TYPES.FREELANCER
						? data.freelanceDocument?.path || data?.freelanceDocument
						: null,
				civilID:
					data.invoiceType === ACCOUNT_TYPES.FREELANCER || data.invoiceType === ACCOUNT_TYPES.COMPANY
						? data.civilID?.path || data?.civilID
						: null,
				invoiceType: data.invoiceType,
				address: data.billingInfo
			};
			const response = await axios.post(COMPANY.UPDATE_GENERAL_SETTINGS, {
				ID: user?.generalSettings?.id,
				address: {
					text: data?.address?.text || user?.generalSettings?.address?.text
				},
				...payload
			});
			if (response.status === 200) {
				const { data } = await axios.post(COMPANY.GET_GENERAL_SETTINGS);
				dispatch(setUserGeneralSettings({ ...payload, ...data }));
				nextStep();
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			throw error;
		}
	};

	const createWarehouse = async (data) => {
		if (data.hasWarehouse) {
			nextStep();
			return;
		}
		try {
			const response = await axios.post(WAREHOUSES.ADD_WAREHOUSE, {
				name: 'my warehouse',
				code: 'my-warehouse',
				person: user.fullName,
				email: user.email,
				phone: data.phone,
				status: 'active',
				address: {
					...data?.address,
					countryShortCode: data?.address.countryName ?? null
				},
				autoPickerAssignment: false,
				userGroupPicking: null,
				servingRadius: null,
				invoiceAddress: null,
				differentAddressForInvoice: false,
				vatNumber: null,
				autoShipment: false,
				storeID: null,
				warehouseUsers: null,
				driverUsers: null
			});
			if (response.status === 200) {
				setValue('hasWarehouse', true);
				dispatch(
					setUserDetails({
						generalSettings: {
							...user.generalSettings,
							defaultWarehouse: response.data.ID
						}
					})
				);
				nextStep();
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			throw error;
		}
	};

	const updateBankInfo = async (data) => {
		if (user.generalSettings?.BankIban) {
			nextStep();
			return;
		}
		try {
			const payload = {};
			Object.keys(TABS[tab].schema.fields).forEach((field) => {
				payload[field] = data[field];
			});
			const response = await axios.post(OTO_LINK.UPDATE_BANK_INFO, {
				...payload
			});
			if (response.status === 200) {
				dispatch(setUserGeneralSettings(payload));
				await updateAccountType(data);
				nextStep();
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			throw error;
		}
	};

	useEffect(() => {
		if (TABS[tab].name === 'payment') {
			dispatch(getOTOLinkSettings());
		}
	}, [tab]);

	const updateOTOLinkSettings = async (data) => {
		try {
			const endpoint = otoLinkSettings?.linkSettingsId
				? OTO_LINK.UPDATE_OTO_LINK_SETTINGS
				: OTO_LINK.ADD_OTO_LINK_SETTINGS;
			const response = await axios.post(endpoint, {
				linkSettingsId: otoLinkSettings?.linkSettingsId,
				allowedDCIds: data.allowedDCIds.filter((id) => deliveryCompanies.some((dc) => dc.ID === id)),
				paymentMethod: data.paymentMethod.length > 1 ? 'BOTH' : data.paymentMethod[0],
				pickupOptions: data.pickupType.length > 1 ? 'BOTH' : data.pickupType[0]
			});

			if (response.status === 200) {
				dispatch(getOTOLinkSettings());
				if (TABS.length - 1 === tab) {
					showSuccess();
					modal.remove();
				} else {
					nextStep();
				}
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.log(error);
			showError();
		}
	};

	const TABS = [
		{
			name: 'verificationAndAddress',
			component: () => <VerificationAndAddress />,
			schema: verificationAndAddressSchema,
			onSubmit: createWarehouse,
			isSkippable: false
		},
		{
			name: 'accountType',
			component: () => <AccountType />,
			schema: accountTypeSchema,
			onSubmit: updateAccountType,
			isSkippable: true
		},
		{
			name: 'bankDetails',
			component: () => <BankDetails />,
			schema: bankDetailsSchema,
			onSubmit: updateBankInfo,
			isSkippable: true
		},
		{
			name: 'payment',
			component: () => <PaymentMethodTab />,
			schema: paymentMethodSchema,
			onSubmit: updateOTOLinkSettings,
			isSkippable: true
		},
		{
			name: 'shippingCompany',
			component: () => <ShippingMethodTab />,
			schema: shippingCompanySchema,
			onSubmit: updateOTOLinkSettings,
			isSkippable: false
		}
	];

	const getUnValidStep = useCallback(
		(hasWarehouse) => {
			const generalSettings = user?.generalSettings || {};

			if (!user.mobileVerified || !hasWarehouse) {
				setTab(0);
				return;
			}

			if (generalSettings?.invoiceType !== ACCOUNT_TYPES.PERSONAL) {
				if (
					(!generalSettings?.CRNumber && !generalSettings?.civilID) ||
					(generalSettings?.invoiceType === ACCOUNT_TYPES.COMPANY && !generalSettings?.CRDocumentURL)
				) {
					setTab(1);
					return;
				}
			}

			if (generalSettings?.invoiceType !== ACCOUNT_TYPES.PERSONAL) {
				if (
					!generalSettings?.civilID &&
					((ACCOUNT_TYPES.COMPANY === generalSettings?.invoiceType &&
						(!generalSettings?.CRNumber || !generalSettings?.CRDocumentURL)) ||
						(ACCOUNT_TYPES.FREELANCER === generalSettings?.invoiceType &&
							!generalSettings?.freelanceDocument))
				) {
					setTab(1);
					return;
				}
			}

			if (!generalSettings?.BankName || !generalSettings?.BankHolderName || !generalSettings?.BankIban) {
				setTab(2);
				return;
			}
			if (!otoLinkSettings?.allowedPaymentMethods) {
				setTab(3);
				return;
			}

			if (otoLinkSettings?.allowedDCs?.length === 0 || !otoLinkSettings?.allowedDCs) {
				setTab(4);
			}
		},
		[user]
	);

	const fillForm = useCallback(async () => {
		const generalSettings = user?.generalSettings || {};
		const defaultValues = {
			isMobileVerified: user?.mobileVerified,
			phone: user?.phone,
			hasWarehouse: false,
			address: {
				...generalSettings?.address,
				text: null,
				countryName: user?.localization?.name,
				stateName: null,
				cityName: null,
				districtName: null,
				street: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null,
				addressInputType: null
			},
			// account type
			invoiceType: generalSettings.invoiceType,
			VATNumber: generalSettings.VATNumber,
			CRNumber: generalSettings.CRNumber,
			companyName: generalSettings.companyName,
			taxOffice: generalSettings.taxOffice,
			notVATRegistered: generalSettings.notVATRegistered,
			freelanceDocument: generalSettings.freelanceDocument,
			CRDocumentURL: generalSettings.CRDocumentURL,
			civilID: generalSettings.civilID,
			fullName: generalSettings.fullName,
			idNumber: generalSettings.idNumber,
			// bank details
			BankName: generalSettings.BankName,
			BankHolderName: generalSettings.BankHolderName,
			BankIban: generalSettings.BankIban,
			// payment method
			paymentMethod:
				otoLinkSettings?.paymentMethod === 'BOTH' ? ['CC', 'COD'] : [otoLinkSettings?.paymentMethod || 'COD'],
			// shipping company
			allowedDCIds: otoLinkSettings?.allowedDCs || [],
			pickupType: ['branchDropOff', 'pickupByDc'],
			billingInfo: billingDefaults.address
		};

		try {
			const response = await axios.post(WAREHOUSES.GET_WAREHOUSE, {
				ID: user?.generalSettings?.defaultWarehouse
			});
			if (response.status === 200) {
				defaultValues.hasWarehouse = true;
				defaultValues.address = {
					...defaultValues.address,
					...response.data?.address
				};
				getUnValidStep(true);
			} else {
				throw new Error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			getUnValidStep(false);
		}

		return defaultValues;
	}, [user]);

	const formMethods = useForm({
		resolver: yupResolver(TABS[tab].schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading, defaultValues }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const paymentMethod = watch('paymentMethod');
	const pickupType = watch('pickupType');

	const nextStep = async () => {
		if (tab < TABS.length - 1) {
			setTab(tab + 1);
		}
	};

	const ActiveTab = useMemo(() => TABS[tab].component, [tab]);

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const skipStep = () => {
		Object.keys(TABS[tab].schema.fields).forEach((field) => {
			formMethods.setValue(field, defaultValues[field], { shouldValidate: false, shouldDirty: false });
			clearErrors(field);
		});
		if (tab < TABS.length - 1) {
			setTab(tab + 1);
		} else if (tab === TABS.length - 1) {
			modal.remove();
		}
	};

	useEffect(() => {
		if (paymentMethod?.length && pickupType) {
			dispatch(
				getDeliveryCompanies({
					paymentMethod: paymentMethod.length > 1 ? 'BOTH' : paymentMethod?.[0],
					pickupType: pickupType?.length > 1 ? 'BOTH' : pickupType?.[0]
				})
			);
		}
	}, [paymentMethod, pickupType]);

	return (
		<Modal maxWidth="x-large" fullwidth className="modal-createOTOLink">
			<S.StyledModalTitle>
				<Typography variant="p2" fontWeight="500">
					otoLink
				</Typography>
				<ProgressWizard clickable={false} stages={TABS} setIndex={setTab} index={tab} />
			</S.StyledModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<ActiveTab />
				</Form>
			</ModalContent>

			<ModalActions>
				{TABS[tab].isSkippable && (
					<Button
						variant="blackOutline"
						label="skipThisStep"
						onClick={skipStep}
						css={{
							textDecoration: 'underline',
							border: 'none !important'
						}}
					/>
				)}

				{tab !== 0 && <Button label="previous" variant="outline" icon={faAngleLeft} onClick={prevStep} />}

				<Button
					icon={faAngleRight}
					label={tab === TABS.length - 1 ? 'save' : 'continue'}
					iconPos="right"
					onClick={handleSubmit(TABS[tab].onSubmit, (error) => AppLogger.error(error))}
					variant="blue"
					loading={isLoading}
				/>
			</ModalActions>
		</Modal>
	);
});

export default OTOLinkOnboarding;
